import {
  BulkActionRequest,
  useBulkActionToast,
} from "@/components/AppProvider/AppNotificationProvider/useBulkActionToast";
import React, { useEffect } from "react";
import { Sheet } from "../../../../../domain/Sheet";

import { useAppRouteParams } from "@/AppRoutes";
import { postCopySheet } from "@/api/sheets";
import {
  createNewDisplayNameOrThrow,
  createNewNameOrThrow,
} from "@/helpers/createNewName";
import { translation } from "@/i18n";
import { nsSheet } from "@/i18n/locales/sheet";

const useTranslation = translation.use(nsSheet);

/**
 * 1件のシートを即時複製し、Toast で進捗・結果を通知するフック。
 * BulkActionRequest.phase が "requested" かつ itemIds.length === 1 のときに実行する。
 * APIの /sheets/{sheet_id}/copy エンドポイントを使用してシートを複製する。
 */
export const useSheetDuplicateAction = ({
  sheets,
  duplicateRequest,
  setDuplicateRequest,
  setSelectedSheetIds,
}: {
  sheets: Sheet[];
  duplicateRequest: BulkActionRequest;
  setDuplicateRequest: React.Dispatch<React.SetStateAction<BulkActionRequest>>;
  setSelectedSheetIds: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
  const { t } = useTranslation();
  const { organizationId, versionId } = useAppRouteParams();

  const { runWithToast } = useBulkActionToast({
    inProgress: () => t.sheet.duplicate.inProgress(),
    success: () => t.sheet.duplicate.success(),
    error: () => t.sheet.duplicate.error(),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      duplicateRequest.phase !== "requested" ||
      duplicateRequest.itemIds.length !== 1
    )
      return;

    const duplicateSheet = sheets.find(
      (s) => s.id === duplicateRequest.itemIds[0]
    );
    if (!duplicateSheet) {
      // 対象シートが存在しない場合（他ユーザーによる削除など）は idle に戻して操作を回復する
      setDuplicateRequest({ phase: "idle", itemIds: [] });
      return;
    }

    // 即時 in_progress へ遷移してリマウント時の二重実行を防ぐ
    setDuplicateRequest({
      phase: "in_progress",
      itemIds: duplicateRequest.itemIds,
    });

    const run = async () => {
      const { allSuccess } = await runWithToast([duplicateSheet], async () => {
        const newDisplayName = createNewDisplayNameOrThrow(
          sheets.map((s) => s.displayName),
          duplicateSheet.displayName
        );
        const newName = createNewNameOrThrow(
          sheets.map((s) => s.name),
          duplicateSheet.name
        );

        // APIを使用してシートを複製
        await postCopySheet({
          organizationId,
          versionId,
          sheetId: duplicateSheet.id,
          name: newName,
          displayName: newDisplayName,
        });
      });
      if (allSuccess) setSelectedSheetIds([]);
      setDuplicateRequest({ phase: "idle", itemIds: [] });
    };

    run();
  }, [duplicateRequest.phase]);
};
